import { Injectable } from "@angular/core";
import * as _ from "lodash";

import { DisplayService } from "../../../display/display.service";
import { ADPolicyState } from "src/app/constants/ad-policy.constants";
import { LoggerService } from "src/app/services/logger/logger.service";
import { DeviceSettingsFeatureToggle } from "src/app/enums/feature-toggle.enum";
import { CommonService } from "src/app/services/common/common.service";
import { LocalStorageKey } from "src/app/enums/local-storage-key.enum";
import { EyeCareModeResponse } from "src/app/data-models/camera/camera-detail.model";
import { EyeCareModeCapability } from "src/app/data-models/device/eye-care-mode-capability.model";

@Injectable({
	providedIn: "root",
})
export class DisplayDefaultValueService {
	constructor(
		private displayService: DisplayService,
		private logger: LoggerService,
		private commonService: CommonService
	) { }

	initialize(adTags: any): void {
		const displayTag = _.get(adTags, DeviceSettingsFeatureToggle.DisplayAndCameraDisplay);
		const isDisplayAdPolicyEnabled = displayTag === ADPolicyState.ENABLED;
		this.logger.info("DisplayDefaultValueService - Initialize", {
			displayTag,
			isDisplayAdPolicyEnabled,
		});

		if (isDisplayAdPolicyEnabled) {
			this.setDisplayDefaultValues();
		}
	}

	private setDisplayDefaultValues() {
		const isShellAvaliabled = this.displayService.isShellAvailable;
		this.logger.info("DisplayCameraDefaultValueService - isDisplayCameraAvailable", isShellAvaliabled);

		if (isShellAvaliabled) {
			this.resetDaytimeColorTemperatureAndEyeCareMode();
			this.setPrivacyGuard();
		}
	}

	private async resetDaytimeColorTemperatureAndEyeCareMode(): Promise<void> {
		try {
			await this.displayService.resetEyecaremodeAllSettings();
			await this.displayService.initEyecaremodeSettings();

			this.setDaytimeColorCache();
			this.setEyeCareModeCache();
		}
		catch (error) {
			this.logger.error('DisplayDefaultValueService - resetDaytimeColorTemperatureAndEyeCareMode - Error', error);
		}
	}

	private async setDaytimeColorCache() {
		try {
			const dayTimeColorTemperature: any = await this.displayService.getDaytimeColorTemperature();
			const eyeCareModeResponse = {
				available: dayTimeColorTemperature.available,
				current: dayTimeColorTemperature.current,
				maximum: dayTimeColorTemperature.maximum,
				minimum: dayTimeColorTemperature.minimum,
				eyeCareMode: dayTimeColorTemperature.eyemodestate
			} as EyeCareModeResponse;

			this.commonService.setLocalStorageValue(LocalStorageKey.DisplayColorTempCapability, eyeCareModeResponse);
		}
		catch (error) {
			this.logger.error('DisplayDefaultValueService - setDaytimeColorCache - Error', error)
		}
	}

	private async setEyeCareModeCache() {
		try {
			const eyeCareCache: EyeCareModeCapability = this.commonService.getLocalStorageValue(LocalStorageKey.DisplayEyeCareModeCapability, undefined);
			if (eyeCareCache) {
				const [eyeCareModeState, eyeCareAutoMode, displayColorTemperature] = await Promise.all([
					this.displayService.getEyeCareModeState(),
					this.displayService.getEyeCareAutoMode(),
					this.displayService.getDisplayColorTemperature()
				]);

				this.logger.info('DisplayCameraDefaultValueService - setEyeCareModeCache', { eyeCareModeState, eyeCareAutoMode, displayColorTemperature });

				eyeCareCache.toggleStatus = eyeCareModeState.status;
				eyeCareCache.enableSlider = eyeCareModeState.status;
				eyeCareCache.sunsetToSunriseStatus.status = eyeCareAutoMode.status;
				eyeCareCache.eyeCareDataSource = displayColorTemperature;

				this.commonService.setLocalStorageValue(LocalStorageKey.DisplayEyeCareModeCapability, eyeCareCache);
			}
		}
		catch (error) {
			this.logger.error('DisplayCameraDefaultValueService - setEyeCareModeCache - Error', error);
		}
	}

	private async setPrivacyGuard() {
		try {
			const privacyGuardCapability = await this.displayService.getPrivacyGuardCapability();
			if (privacyGuardCapability) {
				this.displayService.setPrivacyGuardStatus(false);
				this.displayService.setPrivacyGuardOnPasswordStatus(false);
			}
		}
		catch (error) {
			this.logger.error('DisplayDefaultValueService - setPrivacyGuard - Error', error);
		}
	}
}
